'use client';
import { Suspense, useEffect, useState } from 'react';
import { useBuildPackagesStore } from 'src/stores/build-package-store';

import {
  PACKAGE_STORAGE,
  SELECTED_FLIGHTS_STORAGE,
  SELECTED_HOTEL_STORAGE,
  SELECTED_ROOMS_STORAGE,
} from '@config/storageKeys';
import { useScrollToTopOnMount } from '@hooks/use-scroll-top-on-mount';
import useStorage from '@hooks/useStorage';
import { ILocationResult } from '@components/searchCity';
import { LeagueEvent } from '@interfaces/APITravel';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { useSearchHotelStore } from '@store/search-hotel-store';
import { normalizeDateToUtcNoonIso } from '@v2/utils/date';
import { PageLoader } from '@v2/components';
import {
  Button,
  Skeleton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@v2/ui';
import { HotelPreview } from './components/HotelPreview';
import { HotelDetailsPrefetch } from '../ViewHotel/hooks/use-hotel-details';
import { useMediaQuery } from 'usehooks-ts';
import { getHotelDatesFromSelectedFlight } from '../flights/utils/getHotelDatesFromSelectedFlight';
import { Header, HotelsContent } from './components';
import { MobileBottomHeader } from './components/HotelContent/MobileBottomHeader';
import { MapView } from './components/MapView';
import { TopHotelFilterBar } from './components/TopHotelFilterBar';
import {
  RoomConfigurationPopup,
  type StayDates,
} from './components/RoomConfigurationPopup';
import {
  asRecord,
  confirmedRooms,
  getRoomPrefill,
  readHotelStorage,
  saveRoomConfiguration,
} from './room-prefill';
import { invalidatePropertySearch } from './util';
import {
  coerceHotelSearchArea,
  stadiumLocationFromEvent,
} from './hotel-search-area';
import { useHotelSearchArea } from './hooks/use-hotel-search-area';
import type { RoomConfiguration } from '@components/RoomConfigurator';

type Props = {
  event: LeagueEvent;
  eventId: string;
};

const normalizeHotelDateForComparison = (date?: string | Date | null) => {
  if (!date) return '';

  try {
    return normalizeDateToUtcNoonIso(date);
  } catch {
    return String(date);
  }
};

export function SearchHotelView(props: Props) {
  return <SearchHotelSession key={props.eventId} {...props} />;
}

function SearchHotelSession({ event, eventId }: Props) {
  const storage = useStorage();

  useEffect(() => {
    if (!useBuildPackagesStore.getState()._hasHydrated) {
      useBuildPackagesStore.persist.rehydrate();
    }
  }, []);

  useScrollToTopOnMount();
  const hasHydrated = useBuildPackagesStore((state) => state._hasHydrated);
  const [prefill, setPrefill] = useState<ReturnType<
    typeof getRoomPrefill
  > | null>(null);
  const [isRoomPopupOpen, setRoomPopupOpen] = useState(false);

  // Update room configurations and hotel dates based on flight data
  useEffect(() => {
    if (!hasHydrated || prefill) return;

    const selectedFlights = readHotelStorage(storage, SELECTED_FLIGHTS_STORAGE);
    const buildPackageState = useBuildPackagesStore.getState();
    const packageData = readHotelStorage(storage, PACKAGE_STORAGE);
    const initial = getRoomPrefill({
      storage,
      eventId,
      rooms: buildPackageState.roomConfigurations,
      tickets: buildPackageState.tickets,
    });

    let derivedHotelDates: ReturnType<typeof getHotelDatesFromSelectedFlight> =
      null;
    try {
      if (asRecord(packageData.selected).flights !== false) {
        derivedHotelDates = getHotelDatesFromSelectedFlight(selectedFlights);
      }
    } catch {
      // Invalid stored flight dates must not prevent room configuration.
    }
    const currentHotelStartDate =
      (typeof packageData.hotelStartDate === 'string'
        ? packageData.hotelStartDate
        : '') || buildPackageState.hotelStartDate;
    const currentHotelEndDate =
      (typeof packageData.hotelEndDate === 'string'
        ? packageData.hotelEndDate
        : '') || buildPackageState.hotelEndDate;
    const hotelEndDate =
      derivedHotelDates?.hotelEndDate ?? currentHotelEndDate ?? '';
    const hotelDatesOutOfSync = Boolean(
      derivedHotelDates &&
      (normalizeHotelDateForComparison(currentHotelStartDate) !==
        derivedHotelDates.hotelStartDate ||
        (derivedHotelDates.hotelEndDate !== null &&
          normalizeHotelDateForComparison(currentHotelEndDate) !==
            derivedHotelDates.hotelEndDate))
    );
    const hasPersistedHotelSelection = Boolean(
      storage.getItem(SELECTED_HOTEL_STORAGE) ||
      storage.getItem(SELECTED_ROOMS_STORAGE)
    );
    const shouldAutoSyncHotelDates =
      hotelDatesOutOfSync &&
      !hasPersistedHotelSelection &&
      !packageData.hotelRoomSourceKey;
    const hotelDateUpdates = derivedHotelDates
      ? {
          hotelStartDate: derivedHotelDates.hotelStartDate,
          hotelEndDate,
        }
      : {};

    if (shouldAutoSyncHotelDates) {
      useBuildPackagesStore.setState(hotelDateUpdates);
      useSearchHotelStore.setState({ roomSelections: [] });
      storage.removeItem(SELECTED_HOTEL_STORAGE);
      storage.removeItem(SELECTED_ROOMS_STORAGE);
      storage.setItem(
        PACKAGE_STORAGE,
        JSON.stringify({
          ...packageData,
          ...hotelDateUpdates,
          selected: {
            ...asRecord(packageData.selected),
          },
        })
      );
    }
    const automaticRooms = confirmedRooms(initial.rooms);
    if (automaticRooms) {
      const previousRooms = buildPackageState.roomConfigurations;
      const changed = saveRoomConfiguration({
        storage,
        rooms: automaticRooms,
        previousRooms,
        sourceKey: initial.sourceKey,
      });
      if (changed || previousRooms.length === 0) {
        useBuildPackagesStore.setState({ roomConfigurations: automaticRooms });
      }
      if (changed) {
        useSearchHotelStore.setState({ roomSelections: [] });
        useCheckoutStepsStore.setState({ hotelSelected: false });
      }
    } else if (!confirmedRooms(buildPackageState.roomConfigurations)) {
      // Child ages must be supplied, not invented for an availability request.
      useBuildPackagesStore.setState({ roomConfigurations: [] });
    }
    if (!initial.isConfirmed || !automaticRooms) {
      setRoomPopupOpen(true);
    }
    setPrefill({ ...initial, isConfirmed: Boolean(automaticRooms) });
  }, [hasHydrated, storage, eventId, prefill]);

  const handleConfirm = (
    rooms: RoomConfiguration[],
    dates: StayDates,
    selection?: {
      area: 'stadium' | 'downtown';
      location: ILocationResult | null;
    }
  ) => {
    if (!prefill) return;
    const roomsChanged = saveRoomConfiguration({
      storage,
      rooms,
      sourceKey: prefill.sourceKey,
      previousRooms: useBuildPackagesStore.getState().roomConfigurations,
    });
    const hotelStartDate = normalizeDateToUtcNoonIso(dates.hotelStartDate);
    const hotelEndDate = normalizeDateToUtcNoonIso(dates.hotelEndDate);
    const current = useBuildPackagesStore.getState();
    const datesChanged =
      normalizeHotelDateForComparison(current.hotelStartDate) !==
        hotelStartDate ||
      normalizeHotelDateForComparison(current.hotelEndDate) !== hotelEndDate;

    if (datesChanged) {
      const packageData = readHotelStorage(storage, PACKAGE_STORAGE);
      storage.setItem(
        PACKAGE_STORAGE,
        JSON.stringify({
          ...packageData,
          hotelStartDate,
          hotelEndDate,
          selected: { ...asRecord(packageData.selected), hotel: false },
        })
      );
      storage.removeItem(SELECTED_HOTEL_STORAGE);
      storage.removeItem(SELECTED_ROOMS_STORAGE);
      invalidatePropertySearch();
    }

    const area = selection?.area === 'downtown' ? 'downtown' : 'stadium';
    const stadium =
      current.stadiumLocation ?? stadiumLocationFromEvent(event) ?? null;
    const nextLocation =
      selection?.location ??
      (area === 'stadium' ? stadium : current.location) ??
      current.location;
    const locationChanged =
      nextLocation?.latitude !== current.location?.latitude ||
      nextLocation?.longitude !== current.location?.longitude ||
      area !== current.hotelSearchArea;

    useBuildPackagesStore.setState({
      roomConfigurations: rooms,
      hotelSearchArea: area,
      stadiumLocation: stadium ?? current.stadiumLocation,
      location: nextLocation,
      ...(datesChanged ? { hotelStartDate, hotelEndDate } : {}),
    });
    if (locationChanged && !datesChanged) {
      invalidatePropertySearch();
    }
    if (roomsChanged || datesChanged || locationChanged) {
      useSearchHotelStore.setState({
        roomSelections: [],
        isMapFullScreen: false,
        hotelDrawerSelected: { isOpen: false, hotelId: null },
      });
      useCheckoutStepsStore.setState({ hotelSelected: false });
    }
    setPrefill({ ...prefill, rooms, isConfirmed: true });
    setRoomPopupOpen(false);
  };

  if (!hasHydrated || !prefill) return <PageLoader />;

  return (
    <Suspense fallback={<PageLoader />}>
      <section className="buy-package-height pt-2 md:mx-auto md:max-w-[1600px] md:px-8 md:pt-8">
        {!prefill.isConfirmed && (
          <div className="mx-4 mb-4 rounded-xl border border-primary-200 bg-white p-4">
            <p className="mb-2 text-sm">
              Complete the child ages to see accurate hotel prices.
            </p>
            <Button onClick={() => setRoomPopupOpen(true)}>
              Configure rooms
            </Button>
          </div>
        )}
        <HotelResults event={event} />
        {isRoomPopupOpen && (
          <RoomConfigurationPopup
            isOpen
            event={event}
            initialRooms={prefill.rooms}
            source={prefill.source}
            onConfirm={handleConfirm}
            onClose={() => setRoomPopupOpen(false)}
          />
        )}
      </section>
    </Suspense>
  );
}

function HotelResults({ event }: Pick<Props, 'event'>) {
  const matches = useMediaQuery('(min-width: 1024px)');
  const isMapFullScreen = useSearchHotelStore((state) => state.isMapFullScreen);
  const [hoveredHotelId, setHoveredHotelId] = useState<number | null>(null);
  const [prefetchHotelId, setPrefetchHotelId] = useState<number | null>(null);
  const { downtown, airport, isDowntownLoading } = useHotelSearchArea(event);
  const committedLocation = useBuildPackagesStore((state) => state.location);
  const committedArea = coerceHotelSearchArea(
    useBuildPackagesStore((state) => state.hotelSearchArea)
  );
  const proximityName =
    committedLocation?.name ??
    (committedArea === 'stadium' ? event.venue?.name : undefined);
  // Show the full-screen EliteSportsTour logo loader until the
  // stadium/downtown locations resolve (prefetched from build-package flow).
  if (isDowntownLoading) return <PageLoader />;
  return (
    <div className="pb-44 lg:pb-28">
      <div className="mb-2 lg:mx-4 lg:mb-6 lg:rounded-2xl lg:border lg:border-gray-200 lg:bg-white lg:shadow-sm">
        <Header event={event} />
        <TopHotelFilterBar />
      </div>
      <div className="px-4">
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,11fr)_minmax(0,9fr)]">
          <div className="min-w-0">
            <HotelsContent
              proximityName={proximityName}
              onHoverHotel={setHoveredHotelId}
            />
          </div>
          {matches && (
            <aside
              aria-label="Hotels near your search location"
              className="h-[calc(100dvh-300px)] min-h-[320px] overflow-hidden rounded-2xl border border-gray-200 shadow-sm lg:sticky lg:top-4 lg:h-[calc(100dvh-160px)]"
            >
              <Suspense fallback={<Skeleton className="h-full w-full" />}>
                <MapView
                  className="h-full w-full"
                  event={event}
                  cityCenter={downtown}
                  airport={airport}
                  hoveredHotelId={hoveredHotelId}
                  onPrefetchHotel={setPrefetchHotelId}
                />
              </Suspense>
            </aside>
          )}
        </div>
      </div>
      {!matches && (
        <Dialog
          open={isMapFullScreen}
          onOpenChange={(open) =>
            useSearchHotelStore.setState({ isMapFullScreen: open })
          }
        >
          <DialogContent
            showCloseButton={false}
            className="inset-0 z-50 flex h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none p-0 md:max-w-none"
          >
            <div className="flex shrink-0 items-center justify-between border-b bg-white px-4 pb-3 pt-[max(12px,env(safe-area-inset-top))]">
              <DialogTitle className="text-lg">Explore hotels</DialogTitle>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  useSearchHotelStore.setState({ isMapFullScreen: false })
                }
              >
                Close Map
              </Button>
            </div>
            <DialogDescription className="sr-only">
              Zoom in to explore hotels and tap a price for hotel details.
            </DialogDescription>
            <div className="shrink-0 bg-white">
              <TopHotelFilterBar />
            </div>
            <div className="min-h-0 flex-1">
              <MapView
                className="h-full w-full"
                event={event}
                cityCenter={downtown}
                airport={airport}
                onPrefetchHotel={setPrefetchHotelId}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
      <HotelDetailsPrefetch hotelId={hoveredHotelId ?? prefetchHotelId} />
      <HotelPreview proximityName={proximityName} />
      <MobileBottomHeader />
    </div>
  );
}
